import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Rendered by tests with tsx, which would fall back to the classic JSX runtime: pin the automatic one (as tsc does).
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Check from 'lucide-react/dist/esm/icons/check.mjs';
import { track } from './telemetry.js';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.mjs';
import ChevronsDownUp from 'lucide-react/dist/esm/icons/chevrons-down-up.mjs';
import ChevronsUpDown from 'lucide-react/dist/esm/icons/chevrons-up-down.mjs';
import ListChecks from 'lucide-react/dist/esm/icons/list-checks.mjs';
import Play from 'lucide-react/dist/esm/icons/play.mjs';
import Search from 'lucide-react/dist/esm/icons/search.mjs';
import Star from 'lucide-react/dist/esm/icons/star.mjs';
import X from 'lucide-react/dist/esm/icons/x.mjs';
import { MascotLookError } from './client.js';
import { MascotRotatePanel, MascotRotationBanner, MascotRotationTray } from './MascotRotation.js';
import { allGroupsCollapsed, announceMascotLookChanged, BUILT_IN_MASCOT_LOOK, GENERAL_GROUP_ID, GENERAL_GROUP_NAME, groupCollapsed, MASCOT_LOOK_CHANGED_EVENT, MASCOT_TILE_PLACEHOLDER, mascotLookGroups, mascotLooksModel, readCollapsedGroups, writeCollapsedGroups, } from './mascotLooks.js';
import { filterMascotTiles, MASCOT_ANIMATION_CHOICES, MASCOT_ANIMATION_LABELS, MASCOT_NO_FILTERS, MASCOT_SEARCH_DEBOUNCE_MS, MASCOT_SORT_LABELS, mascotCategoriesPresent, mascotFiltersAvailable, mascotFiltersNarrow, mascotSortsAvailable, sortMascotTiles, } from './mascotGalleryFilters.js';
import { holdMascotRatings, listenForMascotRatings, MASCOT_RATING_APPS, mascotRatingRefusal, mascotRatingWaitMs, ratingSummary, ratingWaitMessage, shareMascotRating, starsText, } from './mascotRatings.js';
import { MascotThumbnailLoader } from './mascotThumbnails.js';
import { KyberCrystal } from './kyberCrystal.js';
import { outfitCardLines } from './outfitCardWords.js';
import { MascotOutfitPreview } from './MascotOutfitPreview.js';
import { activeElementAround, MascotTileGrid } from './mascotTileGrid.js';
/**
 * The thumbnails of the tiles drawn (the rows on screen, MascotTileGrid) that have none yet (a URL-mode view), read by one
 * MascotThumbnailLoader per gallery: batched, a bounded number of requests at a time, each failure tried again on its
 * own timer, every thumbnail kept by image hash for the page. Answers what is known so far, by tile, and re-renders as
 * reads land. A gallery of 692 thus reads a few batches as the person scrolls, never all 22 at mount.
 */
function useTileThumbnails(drawn, readers) {
    const { one, many } = readers;
    const loader = useMemo(() => new MascotThumbnailLoader({ ...(one ? { one } : {}), ...(many ? { many } : {}) }), [one, many]);
    const [, landed] = useState(0);
    useEffect(() => {
        const unsubscribe = loader.subscribe(() => landed((value) => value + 1));
        // On the way out nothing more is wanted (its timers stop); a read in flight still lands in the shared cache.
        return () => { unsubscribe(); loader.setWanted([]); };
    }, [loader]);
    const wanted = drawn.filter((tile) => tile.src === '' && tile.sha256 && loader.get(tile.sha256) === undefined).map((tile) => `${tile.id}:${tile.sha256}`).join(' ');
    useEffect(() => {
        loader.setWanted(wanted ? wanted.split(' ').map((entry) => { const [id, sha256] = entry.split(':'); return { id: id, sha256: sha256 }; }) : []);
    }, [loader, wanted]);
    return (tile) => (tile.src === '' && tile.sha256 ? loader.get(tile.sha256) : undefined);
}
/**
 * The tiles each grid draws, by grid (MascotTileGrid.onRendered), as one list in gallery order: the thumbnails to read.
 * A grid reports whenever its rows change; one that is gone (a group closed) is left out by `gridKeys`, the grids shown.
 */
function useDrawnTiles(gridKeys) {
    const byGrid = useRef(new Map());
    const [drawnTick, setDrawnTick] = useState(0);
    const onRendered = useCallback((gridKey, tiles) => {
        const current = byGrid.current.get(gridKey);
        if (current === tiles || (tiles.length === 0 && (current === undefined || current.length === 0)))
            return;
        if (tiles.length === 0)
            byGrid.current.delete(gridKey);
        else
            byGrid.current.set(gridKey, tiles);
        setDrawnTick((value) => value + 1);
    }, []);
    return { drawn: gridKeys.flatMap((gridKey) => byGrid.current.get(gridKey) ?? []), onRendered, drawnTick };
}
/**
 * Focus a tile or a star and show its whole cell, the tile with its stars: whatever scrolls (the Settings page since 0.15.2,
 * which no longer gives the gallery a scroll of its own; under a chat reply the picker and the conversation) moves only as
 * far as it must, and the cell's scroll margin keeps the focus ring in view.
 */
function focusInView(item) {
    if (!item)
        return;
    item.focus({ preventScroll: true });
    (item.closest('.gyro-mascot-cell') ?? item).scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
}
/**
 * Arrow keys, Home and End move focus among the group's `selector` elements, wrapping around; nothing is chosen. True
 * when the key was one of them.
 */
function moveFocus(event, selector) {
    const items = Array.from(event.currentTarget.querySelectorAll(selector));
    const current = items.indexOf(event.currentTarget.ownerDocument.activeElement);
    if (current < 0)
        return false;
    const next = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? (current + 1) % items.length
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? (current - 1 + items.length) % items.length
            : event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : -1;
    if (next < 0)
        return false;
    event.preventDefault();
    focusInView(items[next]);
    return true;
}
/** The apps gyro-svc serves an outfit's animation preview to (desktopMascot.ts, actions-preview): the desktop app and the Office pane. */
const MASCOT_ACTIONS_PREVIEW_APPS = new Set(['desktop', 'office']);
const STARS = [1, 2, 3, 4, 5];
/**
 * One outfit's stars, under its tile: the person's own, as a radio group of 1 to 5 filled up to their choice, and once they
 * rated it a button that takes the rating back. As in the gallery, selection does not follow focus: the arrow keys, Home
 * and End only move focus, and Enter, Space or a click rates, because a rating is saved to the person's account and
 * counts in everyone's average. Choosing the stars they already gave does nothing.
 */
function OutfitStars({ name, rating, saving, waiting, onRate }) {
    const groupRef = useRef(null);
    const mine = rating.mine;
    return _jsxs("div", { className: "gyro-mascot-rating", children: [_jsx("div", { className: "gyro-mascot-stars", role: "radiogroup", "aria-label": `Your stars for ${name}`, "aria-busy": saving || undefined, ref: groupRef, onKeyDown: (event) => { if (moveFocus(event, '[role="radio"]'))
                    event.stopPropagation(); }, children: STARS.map((stars) => _jsx("button", { type: "button", role: "radio", className: "gyro-mascot-star", "aria-checked": mine === stars, "aria-label": starsText(stars), "aria-disabled": waiting || undefined, "data-on": mine !== null && stars <= mine ? '' : undefined, tabIndex: stars === (mine ?? 1) ? 0 : -1, onClick: () => { if (!waiting && mine !== stars)
                        onRate(stars); }, children: _jsx(Star, { size: 16, "aria-hidden": "true" }) }, stars)) }), mine !== null && _jsx("button", { type: "button", className: "gyro-mascot-unrate", "aria-label": `Remove your stars for ${name}`, title: "Remove your stars", "aria-disabled": waiting || undefined, onClick: () => {
                    if (waiting)
                        return;
                    // This button goes once the rating is taken back: keep focus in the stars, on the ones the person gave.
                    groupRef.current?.querySelector('[aria-checked="true"]')?.focus();
                    onRate(null);
                }, children: _jsx(X, { size: 14, "aria-hidden": "true" }) })] });
}
/** The narrowing part of the filters as a key: when it changes, every matching group shows open again. */
function narrowingKey(filters) {
    return mascotFiltersNarrow(filters)
        ? JSON.stringify([filters.query.trim().toLowerCase(), [...filters.categories].sort(), filters.mine, filters.day, filters.featured, filters.unrated, filters.animation])
        : '';
}
/**
 * The gallery: a radio group of tiles, the built-in Gyro first. Arrow keys, Home and End move focus between tiles;
 * Enter, Space or a click picks one. Selection does not follow focus (the radio pattern's manual variant), because a
 * pick is saved to the person's account and changes their desktop. Clicking the tile that is already the stored pick
 * does nothing; clicking the tile that shows only automatically (IT's featured look) stores it as the pick. On a day
 * outfit's day its tile is marked "Today" and a line above the gallery says what happens: clicking it while Gyro already
 * wears it does nothing; while the person keeps their own outfit (pickHeld), it hands the day back to the day outfit.
 * With star ratings, each outfit's tile says its average and how many rated it, and the person's own stars sit under it
 * (OutfitStars); the built-in Gyro is not an outfit and has none.
 *
 * Above the tiles (owner, 2026-10-11): a search box (name or category, Escape or the x clears it), filters (my picks
 * and the outfits I rated; day outfits; featured; not yet rated; whether the outfit has an animation, as one of All,
 * Animated and Not animated, owner 2026-10-19; any of the categories present) and a sort (name, highest rated, most
 * picked, newest, where the view carries what each needs). While the search or a filter narrows the
 * gallery, every group that still has a tile shows open and the empty ones are hidden; "Collapse all" and "Expand all"
 * apply to what is shown (and, without a search or filter, are remembered as the per-group chevrons are). Every outfit
 * tile has a magnifier that previews it large (MascotOutfitPreview).
 */
export function MascotLookGallery({ view, builtInSrc, saving = null, onPick, onAutomatic, ratings, loadThumbnail, loadThumbnails, loadImage, rotation, loadActionsPreview }) {
    const model = mascotLooksModel(view, builtInSrc);
    const busy = saving !== null;
    const ratingSaving = ratings?.saving ?? null;
    const selectedId = saving?.look ?? model.selectedId;
    const rated = ratings !== undefined && model.tiles.some((tile) => ratings.shown[tile.id] !== undefined);
    const instance = useId();
    const groupRef = useRef(null);
    const searchRef = useRef(null);
    const restoreFocus = useRef(false);
    // The tile that has keyboard focus: its preview button is the one Tab reaches (one Tab stop for the previews, not one a tile).
    const [focusedTile, setFocusedTile] = useState(null);
    // Search, filters and sort. The search text applies MASCOT_SEARCH_DEBOUNCE_MS after the last keystroke; a filter at once.
    const [queryText, setQueryText] = useState('');
    const [filters, setFilters] = useState(MASCOT_NO_FILTERS);
    useEffect(() => {
        const timer = setTimeout(() => setFilters((current) => (current.query === queryText ? current : { ...current, query: queryText })), MASCOT_SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [queryText]);
    const ratingByOutfit = useMemo(() => new Map(view.outfits.flatMap((outfit) => (outfit.rating ? [[outfit.id, outfit.rating]] : []))), [view.outfits]);
    const ratingOf = (outfitId) => ratings?.shown[outfitId] ?? ratingByOutfit.get(outfitId);
    const narrowing = mascotFiltersNarrow(filters);
    const shownTiles = sortMascotTiles(filterMascotTiles(model.tiles, filters, { pick: view.pick, ratingOf }), filters.sort, ratingOf);
    const categories = mascotCategoriesPresent(model.tiles);
    const sorts = mascotSortsAvailable(model.tiles, ratings !== undefined);
    const available = mascotFiltersAvailable(model.tiles, ratings !== undefined);
    const hasOutfits = model.tiles.length > 1;
    // Outfit categories (owner, 2026-10-09): with any, the gallery is one collapsible group per category, General first;
    // which groups are open is remembered in this browser (per person on a desktop). The group holding the selected tile
    // (or today's day outfit) starts open whatever its size, and while the person keeps it closed its header says
    // "Selected", so the pick is never out of sight without a word. Without any category, it is one flat list.
    // While the search or a filter narrows the gallery, the groups shown are those with a matching tile, each open until
    // the person closes it (that choice lasts while the same search and filters hold, and is never remembered).
    const grouped = model.tiles.some((tile) => tile.category);
    const groups = !grouped ? []
        : shownTiles.some((tile) => tile.category) ? mascotLookGroups(shownTiles)
            : [{ id: GENERAL_GROUP_ID, name: GENERAL_GROUP_NAME, tiles: shownTiles }];
    const shownGroups = narrowing ? groups.filter((group) => group.tiles.length > 0) : groups;
    const [collapsed, setCollapsed] = useState(() => readCollapsedGroups());
    const narrowKey = narrowingKey(filters);
    const [narrowed, setNarrowed] = useState({ key: '', chosen: {} });
    const narrowedChosen = narrowed.key === narrowKey ? narrowed.chosen : {};
    const isCollapsed = (group) => (narrowing ? narrowedChosen[group.id] ?? false : groupCollapsed(group, collapsed, selectedId));
    const setGroups = (next) => {
        if (narrowing)
            setNarrowed({ key: narrowKey, chosen: next });
        else {
            setCollapsed(next);
            writeCollapsedGroups(next);
        }
    };
    const toggleGroup = (group) => setGroups({ ...(narrowing ? narrowedChosen : collapsed), [group.id]: !isCollapsed(group) });
    const setAllGroups = (value) => setGroups(allGroupsCollapsed(shownGroups, value, narrowing ? narrowedChosen : collapsed));
    // The tiles on screen, in order: one of them is the Tab stop (the selected tile, else the first), as in a flat gallery.
    const visible = grouped ? shownGroups.flatMap((group) => (isCollapsed(group) ? [] : group.tiles)) : shownTiles;
    const visibleTabStop = Math.max(0, visible.findIndex((tile) => tile.id === selectedId));
    const tabStopId = visible[visibleTabStop]?.id;
    // The grids draw only their rows on screen (MascotTileGrid): the thumbnails by URL (the 2,000 gallery) are read for
    // the tiles drawn, kept across galleries. The tile focus is moving to is drawn first (pinned), then focused.
    const { drawn, onRendered, drawnTick } = useDrawnTiles(grouped ? shownGroups.filter((group) => !isCollapsed(group)).map((group) => group.id) : ['gallery']);
    const thumbnailOf = useTileThumbnails(drawn, { ...(loadThumbnail ? { one: loadThumbnail } : {}), ...(loadThumbnails ? { many: loadThumbnails } : {}) });
    const [pendingFocus, setPendingFocus] = useState(null);
    // Outfit rotation (owner, 2026-10-19; mockups gallery-multiselect, rotate-panel, rotating-state): "Pick several" turns
    // every tile's click into a tick, numbered in the order ticked; the tray and the Rotate panel act on those ticks; while
    // a loop is on, the banner stands above the gallery and the loop's tiles keep their numbers, the one showing now
    // outlined. Edit... reopens the panel with the loop's outfits ticked. Offered only where gyro-svc carries the loop.
    const loop = rotation?.loop ?? null;
    const [multi, setMulti] = useState(false);
    const [picked, setPicked] = useState([]);
    const [panel, setPanel] = useState(null);
    const outfitIds = useMemo(() => new Set(view.outfits.map((outfit) => outfit.id)), [view.outfits]);
    // A tick of an outfit that left the gallery drops with it.
    useEffect(() => { setPicked((current) => (current.every((id) => outfitIds.has(id)) ? current : current.filter((id) => outfitIds.has(id)))); }, [outfitIds]);
    // A loop started or edited (the view's loop is a new one): the ticks are the loop's now, so the mode ends.
    const startedKey = loop?.startedAtMs ?? 0;
    useEffect(() => { if (startedKey) {
        setMulti(false);
        setPicked([]);
        setPanel(null);
    } }, [startedKey]);
    const tileById = new Map(model.tiles.map((tile) => [tile.id, tile]));
    const loopOrder = new Map((loop?.outfitIds ?? []).map((id, index) => [id, index + 1]));
    const pickedTiles = picked.map((id) => tileById.get(id)).filter((tile) => tile !== undefined);
    const artOf = (tile) => tile.src || thumbnailOf(tile) || '';
    const leaveMulti = () => { setMulti(false); setPicked([]); setPanel(null); };
    const togglePick = (id) => setPicked((current) => (current.includes(id) ? current.filter((known) => known !== id) : [...current, id]));
    const openEdit = () => {
        if (!loop)
            return;
        setMulti(true);
        setPicked(loop.outfitIds.filter((id) => outfitIds.has(id)));
        setPanel({ editing: loop });
    };
    // Drawn wherever the window is: the tab stop, the tile focus is moving to, and the focused tile (a focus the scroll
    // left behind must not go with its row, which would hand focus to the page).
    const pinned = [tabStopId, pendingFocus, focusedTile].filter((id) => typeof id === 'string');
    const tileElement = (tileId) => groupRef.current?.querySelector(`.gyro-mascot-cell[data-tile="${tileId}"] > .gyro-mascot-tile`) ?? null;
    useLayoutEffect(() => {
        if (pendingFocus === null)
            return;
        const element = tileElement(pendingFocus);
        if (element)
            focusInView(element);
        if (element || !visible.some((tile) => tile.id === pendingFocus))
            setPendingFocus(null);
    }, [pendingFocus, drawnTick]); // eslint-disable-line react-hooks/exhaustive-deps -- a tile that is not on screen is pinned until it is drawn (drawnTick)
    /**
     * Arrow keys, Home and End move focus among every tile of the gallery, in order and wrapping around, whether the next
     * one is drawn yet or not; nothing is chosen. True when the key was one of them and focus was on a tile.
     */
    const moveTileFocus = (event) => {
        const active = activeElementAround(event.currentTarget);
        if (!active?.classList.contains('gyro-mascot-tile'))
            return false;
        const current = visible.findIndex((tile) => tile.id === active.closest('.gyro-mascot-cell')?.dataset.tile);
        if (current < 0)
            return false;
        const next = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? (current + 1) % visible.length
            : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? (current - 1 + visible.length) % visible.length
                : event.key === 'Home' ? 0 : event.key === 'End' ? visible.length - 1 : -1;
        if (next < 0)
            return false;
        event.preventDefault();
        const target = visible[next].id;
        const element = tileElement(target);
        if (element)
            focusInView(element);
        else
            setPendingFocus(target);
        return true;
    };
    // A save can remove the focused control (choosing automatic hides its own button; a refused pick or rating reloads the
    // gallery, which may drop that tile; ratings switched off hide every star): put focus back on the selected tile rather
    // than losing it.
    useEffect(() => {
        if (busy || ratingSaving !== null || !restoreFocus.current)
            return;
        restoreFocus.current = false;
        const active = groupRef.current && activeElementAround(groupRef.current);
        if (!active || active === active.ownerDocument.body || !active.isConnected)
            setPendingFocus(selectedId ?? null);
    }, [busy, ratingSaving]); // eslint-disable-line react-hooks/exhaustive-deps -- runs when a save ends, with the selection it answered
    const choose = (look) => {
        if (busy)
            return;
        restoreFocus.current = true;
        if (look === null)
            onAutomatic();
        else
            onPick(look);
    };
    const rate = (outfitId, stars) => {
        if (!ratings || ratings.waiting)
            return;
        restoreFocus.current = true;
        ratings.onRate(outfitId, stars);
    };
    const clearSearch = () => {
        setQueryText('');
        setFilters((current) => (current.query === '' ? current : { ...current, query: '' }));
    };
    const clearAll = () => {
        setQueryText('');
        setFilters((current) => ({ ...MASCOT_NO_FILTERS, sort: current.sort }));
        searchRef.current?.focus();
    };
    const toggleFilter = (key) => setFilters((current) => ({ ...current, [key]: !current[key] }));
    const chooseAnimation = (animation) => setFilters((current) => (current.animation === animation ? current : { ...current, animation }));
    // The animation filter is a radio group: arrow keys move among its three chips and choose as they go, like the admin console's.
    const animationKey = (event, index) => {
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
        if (!step)
            return;
        event.preventDefault();
        const next = MASCOT_ANIMATION_CHOICES[(index + step + MASCOT_ANIMATION_CHOICES.length) % MASCOT_ANIMATION_CHOICES.length];
        chooseAnimation(next);
        (event.currentTarget.parentElement?.querySelector(`[data-animation="${next}"]`))?.focus();
    };
    const toggleCategory = (id) => setFilters((current) => {
        const next = new Set(current.categories);
        if (next.has(id))
            next.delete(id);
        else
            next.add(id);
        return { ...current, categories: next };
    });
    const cell = (tile) => {
        const checked = tile.id === selectedId;
        // Outfit rotation: the tile's number in the ticks (Pick several) or in the loop; the built-in Gyro is never in a loop.
        const loopable = tile.id !== BUILT_IN_MASCOT_LOOK;
        const order = multi ? picked.indexOf(tile.id) + 1 : loopOrder.get(tile.id) ?? 0;
        const ticked = multi && order > 0;
        const detail = busy && checked && saving?.look === tile.id ? 'Saving…' : tile.detail;
        const rating = ratings?.shown[tile.id];
        const summary = rating ? ratingSummary(rating) : null;
        const nameId = `${instance}-${tile.id}-name`;
        const metaId = `${instance}-${tile.id}-meta`;
        const meta = Boolean(tile.today || tile.featured || detail || summary || tile.hasActions);
        const art = tile.src || thumbnailOf(tile) || '';
        // The outfit economy (owner, 2026-10-15): the kyber crystal in the tile's corner, the serial on hover, the card in the preview.
        const card = tile.economy ? outfitCardLines(tile.economy) : null;
        // The image sits in a fixed 56 px box, decoded off the main thread as it lands: the grid never reflows for it.
        return _jsxs("div", { className: "gyro-mascot-cell", "data-tile": tile.id, children: [_jsxs("button", { type: "button", role: multi ? 'checkbox' : 'radio', className: "gyro-mascot-tile", "aria-checked": multi ? ticked : checked, "aria-disabled": multi ? !loopable || undefined : busy || undefined, "aria-labelledby": nameId, "aria-describedby": meta ? metaId : undefined, "data-picked": ticked ? '' : undefined, "data-loop": !multi && order > 0 ? '' : undefined, tabIndex: tabStopId === tile.id ? 0 : -1, onFocus: () => setFocusedTile(tile.id), onClick: () => {
                        if (multi) {
                            if (loopable)
                                togglePick(tile.id);
                            return;
                        }
                        if (tile.id !== view.pick && !(tile.today && tile.id === view.showing))
                            choose(tile.id);
                    }, children: [checked && !multi && _jsx(Check, { className: "gyro-mascot-tile-check", size: 14, strokeWidth: 2.5, "aria-hidden": "true" }), multi && loopable && _jsx("span", { className: "gyro-mascot-tile-pick", "aria-hidden": "true" }), order > 0 && _jsx("span", { className: "gyro-mascot-tile-order", "aria-hidden": "true", children: order }), card && _jsx(KyberCrystal, { rarity: card.rarity, size: 12, className: "gyro-mascot-tile-crystal", title: card.summary }), _jsx("span", { className: "gyro-mascot-tile-art", children: _jsx("img", { src: art || MASCOT_TILE_PLACEHOLDER, alt: tile.name, draggable: false, loading: "lazy", decoding: "async", style: tile.mirrored ? { transform: 'scaleX(-1)' } : undefined }) }), _jsx("span", { className: "gyro-mascot-tile-name", id: nameId, children: tile.name }), meta && _jsxs("span", { className: "gyro-mascot-tile-meta", id: metaId, children: [tile.today && _jsx("span", { className: "gyro-settings-badge gyro-mascot-today", children: "Today" }), tile.featured && _jsx("span", { className: "gyro-settings-badge", children: "Featured" }), tile.hasActions && _jsx("span", { className: "gyro-mascot-tile-actions", role: "img", title: "Has animations", "aria-label": "Has animations", children: _jsx(Play, { size: 10, "aria-hidden": "true" }) }), detail && _jsx("span", { children: detail }), summary && (summary.shown === summary.spoken
                                    ? _jsx("span", { className: "gyro-mascot-tile-rating", children: summary.shown })
                                    : _jsxs("span", { className: "gyro-mascot-tile-rating", children: [_jsx(Star, { size: 10, "aria-hidden": "true" }), _jsx("span", { "aria-hidden": "true", children: summary.shown }), _jsx("span", { className: "gyro-sr-only", children: summary.spoken })] }))] })] }), _jsx(MascotOutfitPreview, { outfitId: tile.id, name: tile.name, thumbnail: art, sha256: tile.sha256, tabIndex: focusedTile === tile.id ? 0 : -1, loadImage: tile.id === BUILT_IN_MASCOT_LOOK ? undefined : loadImage, hasActions: tile.hasActions === true, loadActionsPreview: tile.id === BUILT_IN_MASCOT_LOOK ? undefined : loadActionsPreview, card: tile.economy ? { economy: tile.economy, ...(tile.hasActions !== undefined ? { hasActions: tile.hasActions } : {}) } : undefined }), rating && _jsx(OutfitStars, { name: tile.name, rating: rating, saving: ratingSaving === tile.id, waiting: ratings?.waiting ?? true, onRate: (stars) => rate(tile.id, stars) })] }, tile.id);
    };
    const galleryClass = `gyro-mascot-gallery${rated ? ' gyro-mascot-gallery-rated' : ''}`;
    const noMatch = narrowing && shownTiles.length === 0;
    const names = new Map(model.tiles.map((tile) => [tile.id, tile.name]));
    return _jsxs(_Fragment, { children: [model.dayNote && _jsx("p", { className: "gyro-settings-description gyro-mascot-day-note", children: model.dayNote }), model.note && _jsx("p", { className: "gyro-settings-description gyro-mascot-note", children: model.note }), rotation && loop && !multi && _jsx(MascotRotationBanner, { loop: loop, names: names, showingName: names.get(view.showing) ?? 'Gyro', busy: rotation.busy, onPause: rotation.onPause, onResume: rotation.onResume, onEdit: openEdit, onStop: rotation.onStop, dateOptions: rotation.dateOptions }), hasOutfits && _jsxs(_Fragment, { children: [_jsxs("div", { className: "gyro-mascot-tools", children: [_jsxs("div", { className: "gyro-settings-search gyro-mascot-search", role: "search", children: [_jsx(Search, { size: 16, "aria-hidden": "true" }), _jsx("input", { ref: searchRef, className: "gyro-settings-search-input", type: "text", "aria-label": "Search outfits", placeholder: "Search outfits", autoComplete: "off", spellCheck: false, value: queryText, onChange: (event) => setQueryText(event.target.value), onKeyDown: (event) => { if (event.key === 'Escape' && queryText) {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            clearSearch();
                                        } } }), queryText && _jsx("button", { type: "button", className: "gyro-mascot-search-clear", "aria-label": "Clear search", onClick: () => { clearSearch(); searchRef.current?.focus(); }, children: _jsx(X, { size: 14, "aria-hidden": "true" }) })] }), sorts.length > 1 && _jsxs("label", { className: "gyro-mascot-sort", children: [_jsx("span", { className: "gyro-sr-only", children: "Sort outfits" }), _jsx("select", { className: "gyro-settings-select", value: filters.sort, onChange: (event) => setFilters((current) => ({ ...current, sort: event.target.value })), children: sorts.map((sort) => _jsx("option", { value: sort, children: MASCOT_SORT_LABELS[sort] }, sort)) })] })] }), _jsxs("div", { className: "gyro-mascot-filters", role: "group", "aria-label": "Filter outfits", children: [_jsx("button", { type: "button", className: "gyro-mascot-filter", "aria-pressed": filters.mine, onClick: () => toggleFilter('mine'), children: "My picks / rated by me" }), available.day && _jsx("button", { type: "button", className: "gyro-mascot-filter", "aria-pressed": filters.day, onClick: () => toggleFilter('day'), children: "Day outfits" }), available.featured && _jsx("button", { type: "button", className: "gyro-mascot-filter", "aria-pressed": filters.featured, onClick: () => toggleFilter('featured'), children: "Featured" }), available.unrated && _jsx("button", { type: "button", className: "gyro-mascot-filter", "aria-pressed": filters.unrated, onClick: () => toggleFilter('unrated'), children: "Not yet rated" }), available.animation && _jsx("div", { className: "gyro-mascot-filter-choice", role: "radiogroup", "aria-label": "Animation", children: MASCOT_ANIMATION_CHOICES.map((choice, index) => _jsx("button", { type: "button", role: "radio", className: "gyro-mascot-filter", "data-animation": choice, "aria-checked": filters.animation === choice, tabIndex: filters.animation === choice ? 0 : -1, onClick: () => chooseAnimation(choice), onKeyDown: (event) => animationKey(event, index), children: MASCOT_ANIMATION_LABELS[choice] }, choice)) }), categories.length > 1 && _jsxs("details", { className: "gyro-mascot-category-filter", children: [_jsxs("summary", { className: "gyro-mascot-filter", children: [filters.categories.size > 0 ? `Categories (${filters.categories.size})` : 'Categories', _jsx(ChevronDown, { size: 13, "aria-hidden": "true" })] }), _jsx("div", { className: "gyro-mascot-category-list", role: "group", "aria-label": "Categories", children: categories.map((choice) => _jsxs("label", { className: "gyro-mascot-category-option", children: [_jsx("input", { type: "checkbox", checked: filters.categories.has(choice.id), onChange: () => toggleCategory(choice.id) }), _jsx("span", { className: "gyro-mascot-category-name", children: choice.name }), _jsxs("span", { className: "gyro-mascot-category-count", children: [_jsx("span", { "aria-hidden": "true", children: choice.count }), _jsx("span", { className: "gyro-sr-only", children: `${choice.count} outfit${choice.count === 1 ? '' : 's'}` })] })] }, choice.id)) })] }), narrowing && _jsxs("button", { type: "button", className: "gyro-mascot-filter gyro-mascot-filter-clear", onClick: clearAll, children: [_jsx(X, { size: 12, "aria-hidden": "true" }), "Clear"] }), rotation && !loop && _jsxs("button", { type: "button", className: "gyro-mascot-filter gyro-mascot-filter-multi", "aria-pressed": multi, onClick: () => { if (multi)
                                    leaveMulti();
                                else
                                    setMulti(true); }, children: [_jsx(ListChecks, { size: 13, "aria-hidden": "true" }), "Pick several"] })] })] }), noMatch && _jsxs("p", { className: "gyro-settings-description gyro-mascot-empty", role: "status", children: ["No outfits match.", _jsx("button", { type: "button", className: "gyro-mascot-automatic", onClick: clearAll, children: "Clear the search and filters" })] }), !noMatch && !grouped && _jsx(MascotTileGrid, { gridKey: "gallery", tiles: shownTiles, rated: rated, pinned: pinned, cell: cell, onRendered: onRendered, className: galleryClass, "aria-label": "Gyro\u2019s outfits", "aria-busy": busy, gridRef: groupRef, onKeyDown: (event) => { moveTileFocus(event); } }), !noMatch && grouped && _jsxs(_Fragment, { children: [_jsxs("div", { className: "gyro-mascot-group-tools", children: [_jsxs("button", { type: "button", className: "gyro-settings-button gyro-mascot-group-tool", onClick: () => setAllGroups(true), children: [_jsx(ChevronsDownUp, { size: 14, "aria-hidden": "true" }), "Collapse all"] }), _jsxs("button", { type: "button", className: "gyro-settings-button gyro-mascot-group-tool", onClick: () => setAllGroups(false), children: [_jsx(ChevronsUpDown, { size: 14, "aria-hidden": "true" }), "Expand all"] })] }), _jsx("div", { className: "gyro-mascot-gallery-groups", ref: groupRef, onKeyDown: (event) => { moveTileFocus(event); }, children: shownGroups.map((group) => {
                            const open = !isCollapsed(group);
                            const panelId = `${instance}-group-${group.id}`;
                            const count = group.tiles.length;
                            // A closed group that hides the selected tile says so on its header.
                            const hidesSelection = !open && group.tiles.some((tile) => tile.id === selectedId);
                            return _jsxs("section", { className: "gyro-mascot-group", "aria-label": group.name, children: [_jsxs("button", { type: "button", className: "gyro-mascot-group-head", "aria-expanded": open, "aria-controls": open ? panelId : undefined, onClick: () => toggleGroup(group), children: [_jsx(ChevronDown, { className: "gyro-mascot-group-chevron", size: 16, "aria-hidden": "true" }), _jsx("span", { className: "gyro-mascot-group-name", children: group.name }), hidesSelection && _jsx("span", { className: "gyro-settings-badge gyro-mascot-group-selected", children: "Selected" }), _jsxs("span", { className: "gyro-mascot-group-count", children: [_jsx("span", { "aria-hidden": "true", children: count }), _jsx("span", { className: "gyro-sr-only", children: `${count} outfit${count === 1 ? '' : 's'}` })] })] }), open && _jsx(MascotTileGrid, { gridKey: group.id, id: panelId, tiles: group.tiles, rated: rated, pinned: pinned, cell: cell, onRendered: onRendered, className: galleryClass, "aria-label": `${group.name} outfits`, "aria-busy": busy })] }, group.id);
                        }) })] }), model.noOutfits && _jsx("p", { className: "gyro-settings-description", children: "IT hasn\u2019t added any outfits yet." }), multi && _jsx(MascotRotationTray, { picked: pickedTiles, artOf: artOf, onClear: () => setPicked([]), onRotate: () => setPanel({ editing: loop }) }), panel && _jsx(MascotRotatePanel, { picked: pickedTiles, artOf: artOf, editing: panel.editing, busy: rotation?.busy ?? false, onStart: (definition) => rotation?.onStart(definition), onCancel: () => { if (panel.editing)
                    leaveMulti();
                else
                    setPanel(null); }, dateOptions: rotation?.dateOptions }), model.offerAutomatic && !multi && _jsx("button", { type: "button", className: "gyro-mascot-automatic", "aria-disabled": busy || undefined, onClick: () => choose(null), children: "Use IT\u2019s featured outfit automatically" })] });
}
/**
 * Settings > Gyro’s outfits (and the outfit picker under a chat reply, which is this same gallery): loads the gallery when
 * opened and saves a pick to the person's account, then announces it so the agent redraws the bubble. Every other
 * mounted gallery reloads when one announces, so a pick made in chat also shows in Settings and in earlier pickers; a
 * gallery never reloads on its own announcement, because its save already answered the new view.
 *
 * Star ratings (owner, 2026-10-06), only in the apps gyro-svc takes them from and only while the view carries them: a
 * choice shows at once and is saved; when it is refused, the stars from before come back and one plain line says why.
 * After a rating the gallery shows the numbers the rating's own answer carries (an answer with the person's stars alone
 * keeps the average and count from before), and every other gallery of the same client shows them too, without reading
 * the gallery again. Nothing about a rating is logged, reported or announced outside the page.
 */
export function DesktopMascotLooks({ client, builtInSrc, onNotice }) {
    const [view, setView] = useState(null);
    const [loading, setLoading] = useState(true);
    const [revision, setRevision] = useState(0);
    const [saving, setSaving] = useState(null);
    const [rotationBusy, setRotationBusy] = useState(false);
    const [error, setError] = useState('');
    const liveRef = useRef(true);
    const announcingRef = useRef(false);
    // Bumped by every save: a load that started before a save must not replace the view that save answered.
    const savesRef = useRef(0);
    const canRate = MASCOT_RATING_APPS.has(client.app);
    // The gallery's readers (the 2,000 gallery, and the hover preview): stable per client, so the gallery's loader lives as
    // long as it does. The batch and image reads only from a client that has them (a host's older GyroClient may not).
    const loadThumbnail = useCallback((outfitId) => client.getMascotThumbnail(outfitId), [client]);
    const loadThumbnails = useMemo(() => (typeof client.getMascotThumbnails === 'function' ? (outfitIds) => client.getMascotThumbnails(outfitIds) : undefined), [client]);
    const loadImage = useMemo(() => (typeof client.getMascotImage === 'function' ? (outfitId) => client.getMascotImage(outfitId) : undefined), [client]);
    // The animation preview only where gyro-svc serves it (/v1/mascot/outfits/:id/actions-preview: the desktop app and the
    // Office pane). Gyro on the web shows the outfit, never a Play animation button that could only fail.
    const loadActionsPreview = useMemo(() => (MASCOT_ACTIONS_PREVIEW_APPS.has(client.app) && typeof client.getMascotActionsPreview === 'function'
        ? (outfitId) => client.getMascotActionsPreview(outfitId) : undefined), [client]);
    // Each outfit's rating as its last answer gave it, newer than the view's (the contract: use the rating's own numbers).
    const [answered, setAnswered] = useState({});
    // The rating being saved, shown at once until it is refused (`shown`); null when none is.
    const [rating, setRating] = useState(null);
    const [ratingsHidden, setRatingsHidden] = useState(false);
    // Until when this gallery's stars wait after a 429 (epoch ms; 0: they do not), and the line that says so.
    const [waitUntil, setWaitUntil] = useState(0);
    const waitLineRef = useRef('');
    useEffect(() => {
        liveRef.current = true;
        return () => { liveRef.current = false; };
    }, []);
    useEffect(() => {
        let cancelled = false;
        const saves = savesRef.current;
        setLoading(true);
        void (async () => {
            try {
                const next = await client.getMascotLooks();
                if (!cancelled && saves === savesRef.current)
                    setView(next);
            }
            catch {
                /* the error state below offers a retry; a failed reload keeps the gallery already shown */
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [client, revision]);
    // The event carries no pick, so the gallery reloads. Several in one task land in one render, so in one load.
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const reload = () => { if (!announcingRef.current)
            setRevision((value) => value + 1); };
        window.addEventListener(MASCOT_LOOK_CHANGED_EVENT, reload);
        return () => window.removeEventListener(MASCOT_LOOK_CHANGED_EVENT, reload);
    }, []);
    // Every rating saved through this client, by this gallery or another one (Settings and the chat's pickers agree).
    useEffect(() => listenForMascotRatings(client, (answer) => {
        setAnswered((previous) => {
            const before = previous[answer.outfitId];
            const next = 'count' in answer.rating
                ? { mine: answer.rating.mine, average: answer.rating.average, count: answer.rating.count }
                : { mine: answer.rating.mine, ...(before?.count !== undefined ? { average: before.average, count: before.count } : {}) };
            return { ...previous, [answer.outfitId]: next };
        });
    }), [client]);
    // After a 429 the stars wait until gyro-svc's retry-after has passed; then the line that gave the seconds goes too.
    useEffect(() => {
        if (waitUntil === 0)
            return undefined;
        const timer = setTimeout(() => {
            setWaitUntil(0);
            setError((line) => line === waitLineRef.current ? '' : line);
        }, Math.max(0, waitUntil - Date.now()));
        return () => clearTimeout(timer);
    }, [waitUntil]);
    const save = async (look) => {
        if (!view || saving)
            return;
        savesRef.current += 1;
        setSaving({ look });
        setError('');
        onNotice('');
        try {
            const next = await client.setMascotLook(look);
            track('outfit_changed', { kind: look ? 'outfit' : 'built-in' });
            // The pick is stored either way: tell the agent even if this window has closed meanwhile.
            announcingRef.current = true;
            try {
                announceMascotLookChanged();
            }
            finally {
                announcingRef.current = false;
            }
            if (!liveRef.current)
                return;
            setView(next);
            onNotice('Saved. Your desktop Gyro changes in a moment.');
        }
        catch (failure) {
            if (!liveRef.current)
                return;
            if (failure instanceof MascotLookError && failure.unavailable) {
                setError('That outfit is no longer available.');
                try {
                    const next = await client.getMascotLooks();
                    if (liveRef.current)
                        setView(next);
                }
                catch { /* keep the gallery already shown */ }
            }
            else {
                setError('Your choice could not be saved. Check your connection and try again.');
            }
        }
        finally {
            if (liveRef.current)
                setSaving(null);
        }
    };
    /**
     * Outfit rotation's requests (start or edit, pause, resume, stop): each answers the new view, which shows at once, and
     * announces the change so the agent re-reads the pick and polls (the loop's outfit shows within seconds). An outfit that
     * left the gallery meanwhile (409) says so and reloads the gallery; any other failure keeps what is shown.
     */
    const rotate = async (run) => {
        if (!view || saving || rotationBusy)
            return;
        savesRef.current += 1;
        setRotationBusy(true);
        setError('');
        onNotice('');
        try {
            const next = await run();
            track('outfit_changed', { kind: 'rotation' });
            announcingRef.current = true;
            try {
                announceMascotLookChanged();
            }
            finally {
                announcingRef.current = false;
            }
            if (!liveRef.current)
                return;
            setView(next);
            onNotice('Saved. Your desktop Gyro changes in a moment.');
        }
        catch (failure) {
            if (!liveRef.current)
                return;
            if (failure instanceof MascotLookError && failure.unavailable) {
                setError('One or more of those outfits are no longer available.');
                try {
                    const next = await client.getMascotLooks();
                    if (liveRef.current)
                        setView(next);
                }
                catch { /* keep the gallery already shown */ }
            }
            else {
                setError('The rotation could not be saved. Check your connection and try again.');
            }
        }
        finally {
            if (liveRef.current)
                setRotationBusy(false);
        }
    };
    const rate = async (outfitId, stars) => {
        if (!view || rating)
            return;
        setError('');
        onNotice('');
        const wait = mascotRatingWaitMs(client);
        if (wait > 0) {
            // Another gallery of this client was told to wait: so is this one, without sending anything.
            waitLineRef.current = ratingWaitMessage(wait);
            setError(waitLineRef.current);
            setWaitUntil(Date.now() + wait);
            return;
        }
        setRating({ outfitId, stars, shown: true });
        try {
            shareMascotRating(client, await client.rateMascotOutfit(outfitId, stars));
        }
        catch (failure) {
            const refusal = mascotRatingRefusal(failure);
            if (refusal.waitMs !== undefined)
                holdMascotRatings(client, refusal.waitMs);
            if (!liveRef.current)
                return;
            // The stars from before, at once; the stars keep waiting until any reload below has answered.
            setRating((current) => current && { ...current, shown: false });
            setError(refusal.message);
            if (refusal.hide)
                setRatingsHidden(true);
            if (refusal.waitMs !== undefined) {
                waitLineRef.current = refusal.message;
                setWaitUntil(Date.now() + refusal.waitMs);
            }
            if (refusal.reload) {
                // Whether it was saved, or whether the outfit is still in the gallery, is not known here: read what gyro-svc holds.
                setAnswered((previous) => {
                    if (!Object.hasOwn(previous, outfitId))
                        return previous;
                    const next = { ...previous };
                    delete next[outfitId];
                    return next;
                });
                const saves = savesRef.current;
                try {
                    const next = await client.getMascotLooks();
                    if (liveRef.current && saves === savesRef.current)
                        setView(next);
                }
                catch { /* keep the gallery already shown, with the stars from before */ }
            }
        }
        finally {
            if (liveRef.current)
                setRating(null);
        }
    };
    if (!view) {
        return loading
            ? _jsx("div", { className: "gyro-settings-notice", role: "status", children: "Loading Gyro\u2019s outfits\u2026" })
            : _jsxs("div", { className: "gyro-settings-error", role: "alert", children: ["Gyro\u2019s outfits could not be loaded. Check your connection and try again.", _jsx("button", { type: "button", className: "gyro-settings-button", onClick: () => setRevision((value) => value + 1), children: "Try again" })] });
    }
    const shown = {};
    if (canRate && !ratingsHidden) {
        for (const outfit of view.outfits) {
            if (!outfit.rating)
                continue;
            const known = answered[outfit.id];
            shown[outfit.id] = {
                mine: rating?.outfitId === outfit.id && rating.shown ? rating.stars : known ? known.mine : outfit.rating.mine,
                average: known?.count !== undefined ? known.average ?? null : outfit.rating.average,
                count: known?.count ?? outfit.rating.count,
            };
        }
    }
    const ratings = canRate && !ratingsHidden
        ? { shown, saving: rating?.outfitId ?? null, waiting: rating !== null || waitUntil !== 0, onRate: (outfitId, stars) => void rate(outfitId, stars) }
        : undefined;
    // Outfit rotation: offered only where gyro-svc carries the loop in the view and this client has the requests, and only in
    // the desktop app, whose mascot the loop dresses (Gyro on the web shows the gallery and the pick, never the rotation).
    const rotationControls = view.rotation !== undefined && client.app === 'desktop' && typeof client.setMascotRotation === 'function'
        ? {
            loop: view.rotation,
            busy: rotationBusy,
            onStart: (definition) => void rotate(() => client.setMascotRotation(definition)),
            onPause: () => void rotate(() => client.setMascotRotationPaused(true)),
            onResume: () => void rotate(() => client.setMascotRotationPaused(false)),
            onStop: () => void rotate(() => client.stopMascotRotation()),
        }
        : undefined;
    // The line that says why a pick or a rating was refused comes after the gallery (0.15.2), where the status line that
    // confirms a pick is too. In Settings, whose page scrolls since the gallery has no scroll of its own, both stay at the
    // bottom of the view until the page reaches them, so the person sees them wherever in the gallery they clicked.
    return _jsxs(_Fragment, { children: [_jsx(MascotLookGallery, { view: view, builtInSrc: builtInSrc, saving: saving, onPick: (look) => void save(look), onAutomatic: () => void save(null), ratings: ratings, loadThumbnail: loadThumbnail, loadThumbnails: loadThumbnails, loadImage: loadImage, rotation: rotationControls, loadActionsPreview: loadActionsPreview }), error && _jsx("div", { className: "gyro-settings-error gyro-mascot-status", role: "alert", children: error })] });
}
/**
 * The outfit picker under a chat reply whose `gyro_list_looks` call succeeded (the desktop app only): Settings > Gyro’s
 * outfits itself (its gallery, its save, its stars, its loading and failure lines), with its own status line for the
 * notice that Settings shows in its window. One tap stores the pick and the desktop bubble changes within seconds. It
 * loads the gallery when shown; the tool's own answer never reaches the widget.
 */
export function ChatMascotLookPicker({ client, builtInSrc }) {
    const [notice, setNotice] = useState('');
    return _jsxs("div", { className: "gyro-chat-look-picker", children: [_jsx(DesktopMascotLooks, { client: client, builtInSrc: builtInSrc, onNotice: setNotice }), _jsx("p", { className: "gyro-chat-look-picker-status", role: "status", children: notice })] });
}
//# sourceMappingURL=DesktopMascotLooks.js.map